import React, { useState } from "react";
import { Button, ButtonProps } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import JSZip from 'jszip';
import { AxiosError } from "axios";
import { EsgExportIcon } from "assets";
import { useNotify } from "hooks";

interface ExportButtonProps extends Omit<ButtonProps, 'onClick'> {
    /** Function to call the export API */
    exportFunction: (params: any) => Promise<any>;
    /** Parameters to pass to the export function */
    exportParams: any;
    /** Title to display in the export file */
    exportTitle?: string;
    /** Text to display when not exporting */
    exportText?: string;
    /** Text to display when exporting */
    exportingText?: string;
    /** Success message to show after export */
    successMessage?: string;
    /** Error message to show on failure */
    errorMessage?: string;
    /** Whether to process the response as a ZIP file */
    isZipFile?: boolean;
    /** File extension to filter in ZIP (e.g., '.csv') */
    fileExtension?: string;
    /** Custom success callback */
    onSuccess?: (response: any) => void;
    /** Custom error callback */
    onExportError?: (error: AxiosError) => void;
}

export const ExportButton: React.FC<ExportButtonProps> = ({
    exportFunction,
    exportParams,
    exportTitle = "export",
    exportText = "Export",
    exportingText = "Exporting...",
    successMessage = "Export completed successfully.",
    errorMessage = "Failed to export.",
    isZipFile = true,
    fileExtension = ".csv",
    onSuccess,
    onExportError,
    children,
    disabled,
    ...buttonProps
}) => {
    const [isExporting, setIsExporting] = useState(false);
    const notify = useNotify();

    const { mutateAsync: exportData } = useMutation({
        mutationFn: exportFunction,
        onMutate: () => {
            setIsExporting(true);
        },
        onSuccess: async (response) => {
            try {
                if (isZipFile) {
                    await processZipFile(response, fileExtension);
                } else {
                    // Handle non-ZIP files (direct download)
                    const blob = new Blob([response], { type: "application/octet-stream" });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `${exportTitle}${fileExtension}`;
                    link.click();
                    URL.revokeObjectURL(url);
                }

                setIsExporting(false);
                notify.success(successMessage);
                onSuccess?.(response);
            } catch (error) {
                setIsExporting(false);
                notify.error('Failed to process the downloaded file.');
                onExportError?.(error as AxiosError);
            }
        },
        onError: (error: AxiosError) => {
            setIsExporting(false);
            const errorMsg = typeof error?.response?.data === 'string'
                ? error?.response?.data
                : errorMessage;
            notify.error(errorMsg);
            onExportError?.(error);
        },
    });

    const processZipFile = async (response: any, extension: string) => {
        const zip = new JSZip();
        const zipContent = await zip.loadAsync(response);

        zipContent.forEach(async (relativePath, file) => {
            if (!file.dir && relativePath.endsWith(extension)) {
                const fileContent = await file.async("string");
                const blob = new Blob([fileContent], { type: "text/csv" });
                const url = URL.createObjectURL(blob);

                const link = document.createElement("a");
                link.href = url;
                link.download = relativePath;
                link.click();
                URL.revokeObjectURL(url);
            }
        });
    };

    const handleExport = () => {
        exportData(exportParams);
    };

    return (
        <Button
            variant="contained"
            startIcon={<EsgExportIcon />}
            onClick={handleExport}
            disabled={disabled || isExporting}
            {...buttonProps}
        >
            {children || (isExporting ? exportingText : exportText)}
        </Button>
    );
}; 